import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { createRow, listRows, removeRow, saveRow, type ListOptions } from '@/api/resources';
import { isSupabaseConfigured } from '@/lib/supabase';

/**
 * One list + create/save/remove pair per api view.
 *
 * Writes invalidate the whole view rather than patching the cache: a write passes through
 * database triggers that derive values the client cannot predict (a generated code, the area
 * of a polygon, a status the approval engine owns), so the row that comes back is the truth
 * and anything stitched together locally would be a guess.
 */
export function useRows<T>(view: string, options: ListOptions = {}, enabled = true): UseQueryResult<T[]> {
  return useQuery({
    queryKey: [view, options],
    queryFn: () => listRows<T>(view, options),
    enabled: enabled && isSupabaseConfigured,
  });
}

export function useSaveRow<T>(view: string, onDone?: (row: T) => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Record<string, unknown> }) =>
      (id ? saveRow<T>(view, id, values) : createRow<T>(view, values)),
    onSuccess: (row, variables) => {
      void queryClient.invalidateQueries({ queryKey: [view] });
      toast.success(variables.id ? 'Saved.' : 'Created.');
      onDone?.(row);
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useRemoveRow(view: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => removeRow(view, id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [view] });
      toast.success('Removed.');
    },
    onError: (error: Error) => toast.error(error.message),
  });
}
